// Hideo - Dropbox (paused: hidden while CLOUD_KEYS.dropbox is empty, docs/deployment.md). App with the "App folder"
// permission: the folder is /Applicazioni/Hideo (for the app the root is ""). Sign-in with PKCE (oauth.ts).
// Ported from the legacy cloud/cloud.js (verbatim copy in tests/fixtures/legacy/cloud.js, golden tests in tests/saving*.test.ts).
import { t } from '../../i18n'
import { CLOUD_KEYS, CS, MEDIA_FOLDERS, cloudErr, type CloudMeta, type CloudService, unless404 } from './common'
import { OAUTH_REDIRECT, pkceToken, type PkceConfig } from './oauth'

const DBX: PkceConfig = {
  id: 'dropbox',
  key: () => CLOUD_KEYS.dropbox,
  authUrl: ({ challenge, state }) => `https://www.dropbox.com/oauth2/authorize?client_id=${encodeURIComponent(CLOUD_KEYS.dropbox)}&response_type=code&token_access_type=offline&code_challenge=${challenge}&code_challenge_method=S256&redirect_uri=${encodeURIComponent(OAUTH_REDIRECT)}&state=${state}`,
  tokenUrl: 'https://api.dropboxapi.com/oauth2/token'
}
// The Dropbox-API-Arg header only accepts ASCII: accented letters are written as \uXXXX
const dbxArg = (o: unknown) => JSON.stringify(o).replace(/[\u007f-￿]/g, c => '\\u' + c.charCodeAt(0).toString(16).padStart(4, '0'))
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function dbx(path: string, body: unknown, { content = false, arg = null as unknown, raw = false } = {}): Promise<any> {
  const token = await pkceToken(DBX, true)
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token }
  if (arg) headers['Dropbox-API-Arg'] = dbxArg(arg)
  if (content && body !== undefined) headers['Content-Type'] = 'application/octet-stream'
  else if (!content && body !== undefined) headers['Content-Type'] = 'application/json'
  const r = await fetch((content ? 'https://content.dropboxapi.com/2' : 'https://api.dropboxapi.com/2') + path,
    { method: 'POST', headers, body: body === undefined ? undefined : (content ? body as BodyInit : JSON.stringify(body)) })
  if (r.status === 401) { CS('dropbox').auth = { ...(CS('dropbox').auth || {}), access: '', exp: 0 }; throw cloudErr(t('Your {service} sign-in has expired: please try again.', { service: 'Dropbox' }), 401) }
  if (!r.ok) { const txt = await r.text(); throw cloudErr(r.status === 409 && /not_found/.test(txt) ? 'not_found' : t('{service} returned an error ({status}).', { service: 'Dropbox', status: r.status }), r.status === 409 && /not_found/.test(txt) ? 404 : r.status, txt) }
  return raw ? r : r.json()
}
interface DropboxEntry { '.tag': string; id: string; name: string; server_modified?: string; rev?: string }
const dNorm = (f: DropboxEntry): CloudMeta => ({ id: f.id, name: f.name, modified: f.server_modified, savedAt: f.server_modified, savedOn: '', version: f.rev })
async function dbxList(path: string): Promise<DropboxEntry[]> {
  let j = await unless404(() => dbx('/files/list_folder', { path, limit: 2000 }), null)
  if (!j) return []
  let all = j.entries
  while (j.has_more) { j = await dbx('/files/list_folder/continue', { cursor: j.cursor }); all = all.concat(j.entries) }
  return all
}
/** Media of a folder, listed once per session: { mediaId: fileId } (the file name contains "[mediaId]") */
const mediaIndex: Record<string, Record<string, string>> = {}

export const CLOUD_DROPBOX: CloudService = {
  id: 'dropbox', name: 'Dropbox', icon: 'fab fa-dropbox', place: t('the "Apps/Hideo" folder in your Dropbox'), configured: () => !!CLOUD_KEYS.dropbox,
  token: interactive => pkceToken(DBX, interactive),
  async connect() {
    await pkceToken(DBX, true)
    let email = ''; try { email = (await dbx('/users/get_current_account', undefined)).email || '' } catch { /* the email is optional */ }
    return { email }
  },
  disconnect() { const a = CS('dropbox').auth; if (a && a.access) fetch('https://api.dropboxapi.com/2/auth/token/revoke', { method: 'POST', headers: { Authorization: 'Bearer ' + a.access } }).catch(() => {}) },
  async listProfiles() {
    return (await dbxList('')).filter(f => f['.tag'] === 'file' && /\.json$/i.test(f.name)).map(dNorm).sort((a, b) => (b.modified || '').localeCompare(a.modified || ''))
  },
  async meta(id) { return unless404(async () => { const f = await dbx('/files/get_metadata', { path: id }); return f['.tag'] === 'file' ? dNorm(f) : null }, null) },
  async findProfile(fileName) { return unless404(async () => dNorm(await dbx('/files/get_metadata', { path: '/' + fileName })), null) },
  async saveProfile(id, fileName, text) {
    // An existing file is overwritten (even if the profile has been renamed in the meantime)
    return dNorm(await dbx('/files/upload', new Blob([text], { type: 'application/json' }), { content: true, arg: { path: id || '/' + fileName, mode: 'overwrite', mute: true } }))
  },
  async readText(id) { return (await dbx('/files/download', undefined, { content: true, arg: { path: id }, raw: true })).text() },
  async findMedia(mediaId) {
    for (const kind of ['img', 'voice'] as const) {
      const key = 'dropbox:' + kind
      if (!mediaIndex[key]) { mediaIndex[key] = {}; (await dbxList('/' + MEDIA_FOLDERS[kind])).forEach(f => { const m = f.name.match(/\[(m_[0-9a-z]+)\]/i); if (m) mediaIndex[key][m[1]] = f.id }) }
      if (mediaIndex[key][mediaId]) return mediaIndex[key][mediaId]
    }
    return ''
  },
  async uploadMedia(kind, fileName, blob) {
    const f = await dbx('/files/upload', blob, { content: true, arg: { path: `/${MEDIA_FOLDERS[kind]}/${fileName}`, mode: 'add', autorename: true, mute: true } })
    return f.id
  },
  async readBlob(id) { const r = await dbx('/files/download', undefined, { content: true, arg: { path: id }, raw: true }); const b = await r.blob(); return { buf: await b.arrayBuffer(), type: b.type } }
}
